import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "reactstrap";

import { Order } from "../../../../services/orderService";

interface OrderCommissionRateChangedBannerProps {
  order: Order;
  enabled: boolean;
}

const bpsToPercentLabel = (bps: number): string => {
  const value = bps / 100;
  return Number.isInteger(value)
    ? String(value)
    : value.toFixed(2).replace(/\.?0+$/, "");
};

const OrderCommissionRateChangedBanner = ({
  order,
  enabled,
}: OrderCommissionRateChangedBannerProps) => {
  const { t } = useTranslation();

  const message = useMemo(() => {
    if (!enabled || order.status !== "open") {
      return null;
    }

    const details: string[] = [];
    const names: string[] = [];

    for (const item of order.order_items ?? []) {
      if (
        item.item_type !== "service" ||
        !item.commission_rate_changed_since_open ||
        item.commission_amount != null
      ) {
        continue;
      }

      const name = item.service?.name?.trim();
      if (!name) {
        continue;
      }

      const toBps =
        item.effective_commission_rate_bps ??
        item.professional?.commission_rate_bps ??
        null;
      const fromBps = item.commission_rate_at_open_bps ?? null;

      if (fromBps != null && toBps != null && fromBps !== toBps) {
        const detail = `${name}: ${bpsToPercentLabel(fromBps)}% → ${bpsToPercentLabel(toBps)}%`;
        if (!details.includes(detail)) {
          details.push(detail);
        }
      } else if (!names.includes(name)) {
        names.push(name);
      }
    }

    if (details.length > 0) {
      return t("studio.orders.commission.rateChangedBannerDetail", {
        details: details.join("; "),
      });
    }

    if (names.length > 0) {
      return t("studio.orders.commission.rateChangedBanner", {
        services: names.join(", "),
      });
    }

    return null;
  }, [enabled, order.order_items, order.status, t]);

  if (!message) {
    return null;
  }

  return (
    <Alert color="warning" className="mb-3">
      {message}
    </Alert>
  );
};

export default OrderCommissionRateChangedBanner;
